/**
 * §9.10 Office & Business Hours — response commitments per channel
 * + operating window / timezone card.
 */

import React from 'react'
import {
  Handshake, Calendar, LifeBuoy, Puzzle, Megaphone, Sparkles,
  Clock, MapPin, Info, Timer,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { RESPONSE_SLAS, OFFICE_HOURS } from '../data'
import { TONE } from '../_shared'

const ICONS = { Handshake, Calendar, LifeBuoy, Puzzle, Megaphone }

export default function Hours() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Office &amp; Business Hours</Eyebrow>
          <SectionHeading align="center"
            title={<>Response times{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">you can rely on.</span></>}
            subtitle="We publish the SLAs we actually meet.  Enterprise support runs on a 24×7 priority lane; standard business channels operate on UTC business hours."
          />
        </div>

        {/* HQ + hours card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
          <StatCard iconKey="MapPin"      tone="orange" label="Headquarters"      value={OFFICE_HOURS.hq}   hint="Global operating base" />
          <StatCard iconKey="Timer"       tone="green"   label="Business hours"     value={OFFICE_HOURS.time}  hint={`${OFFICE_HOURS.days} · ${OFFICE_HOURS.tz}`} />
          <StatCard iconKey="Info"        tone="blue"    label="Support lane"       value="24×7 (Enterprise)"   hint="Priority queues auto-open for Ent tenants" />
        </div>

        {/* Response SLA grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {RESPONSE_SLAS.map((r) => {
            const tone = TONE[r.tone] || TONE.orange
            const Icon = ICONS[r.iconKey] || Sparkles
            return (
              <div key={r.id} className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-xl overflow-hidden flex flex-col gap-2.5">
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 45% at 20% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
                <div className="relative flex items-center justify-between">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon strokeWidth={1.8} style={{ width: 15, height: 15 }} />
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/25 bg-emerald-400/[0.06] px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-emerald-400">
                    <Clock className="h-3 w-3" /> {r.sla}
                  </span>
                </div>
                <div className="relative">
                  <div className="text-[14px] font-semibold text-white leading-tight">{r.label}</div>
                  <p className="text-[12px] text-zinc-400 leading-relaxed mt-1">{r.body}</p>
                </div>
              </div>
            )
          })}
        </div>

        {/* Note */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl px-5 md:px-6 py-4 flex items-start gap-3">
          <Info className="h-4 w-4 text-brand-gold shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-300 leading-relaxed">{OFFICE_HOURS.note}</p>
        </div>
      </Container>
    </section>
  )
}

function StatCard({ iconKey, tone, label, value, hint }) {
  const t = TONE[tone] || TONE.orange
  const Icon = { MapPin, Timer, Info }[iconKey] || Info
  return (
    <div className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-xl overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-40"
        style={{ background: `radial-gradient(80% 45% at 20% 0%, rgba(${t.rgb},0.10), transparent 70%)` }} />
      <div className="relative flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
          style={{ borderColor: `rgba(${t.rgb},0.35)`, background: `rgba(${t.rgb},0.08)`, color: t.hex }}>
          <Icon strokeWidth={1.8} style={{ width: 16, height: 16 }} />
        </span>
        <div>
          <div className="text-[10.5px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
          <div className="text-[15px] font-semibold text-white leading-tight">{value}</div>
          <div className="text-[11.5px] text-zinc-400 leading-relaxed mt-0.5">{hint}</div>
        </div>
      </div>
    </div>
  )
}
